/**
 * Mouse Tracking Hook
 * Handles mouse movement, day snapping, and position calculation
 * Enhanced with performance optimizations and error handling
 */

import { useState, useCallback, RefObject } from 'react';
import type { TimelineViewport, MousePosition } from '../types';
import { dateToPosition, positionToDate, snapToDay } from '../utils';
import { useThrottledCallback } from '../utils/performance';
import { validateViewport, isValidNumber } from '../utils/validation';

interface UseMouseTrackingProps {
  viewport: TimelineViewport;
  innerWidth: number;
  svgRef: RefObject<SVGSVGElement | null>;
  padding?: { top: number; right: number; bottom: number; left: number };
}

export const useMouseTracking = ({
  viewport,
  innerWidth,
  svgRef,
  padding = { top: 20, right: 20, bottom: 70, left: 180 },
}: UseMouseTrackingProps) => {
  const [mousePosition, setMousePosition] = useState<MousePosition>({
    x: 0,
    y: 0,
    date: null,
  });
  const [currentDay, setCurrentDay] = useState<string | null>(null);

  // Performance: Throttle mouse move to max 60fps (16ms)
  const handleMouseMove = useThrottledCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      if (!svgRef.current) return;

      // Validate viewport before processing
      if (!validateViewport(viewport)) {
        console.warn('Invalid viewport data in mouse tracking');
        return;
      }

      try {
        const rect = svgRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left - padding.left;
        const y = event.clientY - rect.top;

        // Validate calculated positions
        if (!isValidNumber(mouseX) || !isValidNumber(y)) return;

        if (mouseX >= 0 && mouseX <= innerWidth) {
          // Step 1: Convert mouse X to raw date
          const rawDate = positionToDate(mouseX, viewport, innerWidth);

          // Step 2: Snap to nearest day boundary (midnight)
          const snappedDate = snapToDay(rawDate);

          // Validate date
          if (!(snappedDate instanceof Date) || isNaN(snappedDate.getTime())) {
            return;
          }

          // Step 3: Get day key for optimization (YYYY-MM-DD)
          const dayKey = snappedDate.toISOString().split('T')[0] || null;

          // Step 4: Only update if day changed (performance optimization)
          if (dayKey !== currentDay) {
            setCurrentDay(dayKey);

            // Step 5: Recalculate X position from snapped date
            const snappedX = dateToPosition(snappedDate, viewport, innerWidth);

            if (isValidNumber(snappedX)) {
              setMousePosition({ x: snappedX, y, date: snappedDate });
            }
          }
        } else {
          setMousePosition({ x: 0, y: 0, date: null });
          setCurrentDay(null);
        }
      } catch (error) {
        console.error('Error in mouse tracking:', error);
        setMousePosition({ x: 0, y: 0, date: null });
        setCurrentDay(null);
      }
    },
    16, // 60fps throttle
    [viewport, innerWidth, currentDay, svgRef]
  );

  const handleMouseLeave = useCallback(() => {
    setMousePosition({ x: 0, y: 0, date: null });
    setCurrentDay(null);
  }, []);

  return {
    mousePosition,
    handleMouseMove,
    handleMouseLeave,
  };
};
